import { employmentStatusLabels } from '../types';

/** The badge kinds in the approved All Employees design. `pending-invite` is styled and ready, but nothing renders
 * it yet: the Employees service has no invitation concept (a separate slice adds it), and the list never infers one. */
export type EmployeeStatusKind = 'active' | 'on-leave' | 'inactive' | 'pending-invite' | 'onboarding';

const KIND_LABELS: Record<EmployeeStatusKind, string> = {
  active: 'Active',
  'on-leave': 'On leave',
  inactive: 'Terminated',
  'pending-invite': 'Pending Invite',
  // The employee accepted the invitation and is filling in their own information, but has not submitted it yet.
  onboarding: 'Onboarding in Progress',
};

/** Maps the real employment status (0 Active, 1 On leave, 2 Terminated) to a badge kind; null for an unknown value. */
function statusKindFor(status: number): EmployeeStatusKind | null {
  if (status === 0) return 'active';
  if (status === 1) return 'on-leave';
  if (status === 2) return 'inactive';
  return null;
}

/** Status is always conveyed by the text label too, never by colour alone. An unknown status renders neutrally with
 * its own label rather than being assumed Active. */
export function EmployeeStatusBadge({ kind, status }: { kind?: EmployeeStatusKind; status?: number }) {
  const resolved = kind ?? (status === undefined ? null : statusKindFor(status));
  const label = resolved ? KIND_LABELS[resolved] : (status !== undefined ? employmentStatusLabels[status] : undefined) ?? 'Unknown';
  return (
    <span className={`status-badge status-badge--${resolved ?? 'unknown'}`}>
      <span className="status-badge__dot" aria-hidden="true" />
      {label}
    </span>
  );
}
